/**
 * NotebookCard - Kitchen/Notebook card for grid view
 *
 * @module NotebookCard
 */

import React, { useState } from 'react';

import { FileText, Clock, MoreVertical, Trash2, ExternalLink } from 'lucide-react';

import ideasKitchenLogo from '../../../assets/ideas-kitchen-logo.png';

const NotebookCard = ({ notebook, onSelect, onDelete, onRename }) => {
  const [showMenu, setShowMenu] = useState(false);

  /**
   * CONFIGURATION: The Color Logic (Enterprise 2026: Glass & Light)
   * LLM is NOT UI — card styling is deterministic from notebook metrics.
   */
  const STATUS_CONFIG = {
    active: {
      label: 'Active',
      color: 'text-amber-700',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      ring: 'ring-amber-100',
      glow: 'shadow-[0_0_22px_-6px_rgba(245,158,11,0.55)]',
      glowBg: 'bg-amber-400/25',
      cardBorder: 'border-amber-100',
      cardGlow: 'shadow-[0_18px_55px_-35px_rgba(245,158,11,0.50)]',
      cardGlowHover: 'hover:shadow-[0_24px_70px_-35px_rgba(245,158,11,0.60)]',
      topLine: 'bg-gradient-to-r from-white via-amber-400 to-white',
      icon: (
        <svg
          className="w-6 h-6 animate-pulse"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"
          />
        </svg>
      ),
    },
    built: {
      label: 'Built',
      color: 'text-purple-700',
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      ring: 'ring-purple-100',
      glow: 'shadow-[0_0_22px_-6px_rgba(168,85,247,0.45)]',
      glowBg: 'bg-purple-400/20',
      cardBorder: 'border-purple-100',
      cardGlow: 'shadow-[0_18px_55px_-35px_rgba(168,85,247,0.45)]',
      cardGlowHover: 'hover:shadow-[0_24px_70px_-35px_rgba(168,85,247,0.55)]',
      topLine: 'bg-gradient-to-r from-white via-purple-400 to-white',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 19V6a2 2 0 012-2h10a2 2 0 012 2v13"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18h12" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 8h8M8 11h8M8 14h6"
          />
        </svg>
      ),
    },
    draft: {
      label: 'Draft',
      color: 'text-sky-700',
      bg: 'bg-sky-50',
      border: 'border-sky-200',
      ring: 'ring-sky-100',
      glow: 'shadow-[0_0_18px_-8px_rgba(59,130,246,0.25)]',
      glowBg: 'bg-sky-400/15',
      cardBorder: 'border-sky-100',
      cardGlow: 'shadow-[0_18px_55px_-38px_rgba(59,130,246,0.25)]',
      cardGlowHover: 'hover:shadow-[0_24px_70px_-38px_rgba(59,130,246,0.30)]',
      topLine: 'bg-gradient-to-r from-white via-sky-400 to-white',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 4h10M10 8h10M6 12h14M6 16h14M6 20h14"
          />
        </svg>
      ),
    },
    archived: {
      label: 'Archived',
      color: 'text-slate-600',
      bg: 'bg-slate-50',
      border: 'border-slate-200',
      ring: 'ring-slate-100',
      glow: 'shadow-none',
      glowBg: 'bg-slate-400/10',
      cardBorder: 'border-slate-100',
      cardGlow: 'shadow-sm',
      cardGlowHover: 'hover:shadow-md',
      topLine: 'bg-gradient-to-r from-white via-slate-300 to-white',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 7H4m2-3h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6" />
        </svg>
      ),
    },
    error: {
      label: 'Burnt',
      color: 'text-rose-600',
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      ring: 'ring-rose-100',
      glow: 'shadow-[0_0_20px_-5px_rgba(225,29,72,0.50)]',
      glowBg: 'bg-rose-400/25',
      cardBorder: 'border-rose-100',
      cardGlow: 'shadow-[0_18px_55px_-35px_rgba(225,29,72,0.35)]',
      cardGlowHover: 'hover:shadow-[0_24px_70px_-35px_rgba(225,29,72,0.45)]',
      topLine: 'bg-gradient-to-r from-white via-rose-400 to-white',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
  };

  const rawCardState = String(notebook?.cardState || '').toLowerCase();
  const fallbackStatus = String(notebook?.status || '').toLowerCase();
  const uiState =
    rawCardState && STATUS_CONFIG[rawCardState]
      ? rawCardState
      : fallbackStatus === 'error'
        ? 'error'
        : fallbackStatus === 'processing'
          ? 'active'
          : 'draft';

  const config = STATUS_CONFIG[uiState] || STATUS_CONFIG.draft;

  const sourceCount = notebook?.sources?.length || notebook?.sourceCount || 0;
  const updatedAt =
    notebook?.lastActivityAt ||
    notebook?.lastAccessedAt ||
    notebook?.updatedAt ||
    notebook?.createdAt ||
    notebook?.createTime
      ? new Date(
          notebook.lastActivityAt ||
            notebook.lastAccessedAt ||
            notebook.updatedAt ||
            notebook.createdAt ||
            notebook.createTime
        ).toLocaleDateString()
      : 'Recently';

  return (
    <div
      className={`group relative w-full bg-white dark:bg-neutral-900 rounded-2xl border ${config.cardBorder} dark:border-neutral-800 ${config.cardGlow} ${config.cardGlowHover} transition-all duration-300 hover:-translate-y-1 overflow-hidden ${
        uiState === 'archived' ? 'opacity-70 grayscale-[0.15]' : ''
      }`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onSelect?.();
      }}
    >
      {/* Top Gradient Line (Subtle hint of color) */}
      <div
        className={`absolute top-0 left-0 right-0 h-1 ${config.topLine} opacity-60 group-hover:opacity-100 transition-opacity duration-500`}
      />

      {/* Menu Button */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={e => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-neutral-800 text-slate-400 hover:text-slate-600 dark:hover:text-neutral-200 transition-colors opacity-0 group-hover:opacity-100"
          title="Actions"
        >
          <MoreVertical className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
        </button>

        {/* Dropdown Menu */}
        {showMenu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
            <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-neutral-800 rounded-xl shadow-lg border border-slate-200 dark:border-neutral-700 py-1 z-20">
              <button
                onClick={e => {
                  e.stopPropagation();
                  onSelect?.();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-neutral-300 hover:bg-slate-50 dark:hover:bg-neutral-700"
              >
                <ExternalLink className="h-4 w-4" />
                Open
              </button>
              {onRename && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onRename?.();
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-neutral-300 hover:bg-slate-50 dark:hover:bg-neutral-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                  Rename
                </button>
              )}
              <button
                onClick={e => {
                  e.stopPropagation();
                  onDelete?.();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </div>
          </>
        )}
      </div>

      <div className="p-6">
        {/* HEADER: Icon & Actions */}
        <div className="flex justify-between items-start mb-4">
          {/* THE METER / ICON CONTAINER */}
          <div
            className={`relative p-3 rounded-xl ${config.bg} ${config.color} border ${config.border} ring-4 ${config.ring} ${config.glow} transition-all duration-500 ${
              uiState === 'active' ? 'animate-pulse' : ''
            }`}
          >
            {/* Inner glow (not clipped / always visible) */}
            <div
              className={`absolute inset-0 rounded-xl ${config.glowBg} blur-xl`}
              aria-hidden="true"
            />
            <div className="relative">{config.icon}</div>
          </div>

          {/* Small kitchen logo watermark */}
          <div className="opacity-70">
            <img
              src={ideasKitchenLogo}
              alt=""
              aria-hidden="true"
              className="h-8 w-8 object-contain"
              draggable={false}
            />
          </div>
        </div>

        {/* CONTENT */}
        <div>
          <h3 className="text-lg font-bold text-slate-800 dark:text-white tracking-tight group-hover:text-amber-600 transition-colors truncate">
            {notebook?.name || notebook?.title || notebook?.displayName || 'Untitled Kitchen'}
          </h3>
          <p className="text-sm text-slate-500 dark:text-neutral-400 mt-1 font-medium truncate">
            {notebook?.description || 'Exploratory intelligence workspace'}
          </p>
          <p className="text-xs text-slate-400 dark:text-neutral-500 mt-2 font-medium">
            Updated {updatedAt}
          </p>
        </div>

        {/* FOOTER / METADATA */}
        <div className="mt-6 pt-4 border-t border-slate-50 dark:border-neutral-800 flex items-center justify-between">
          {/* Status Badge */}
          <span
            className={`text-xs font-bold px-2.5 py-1 rounded-full border ${config.bg} ${config.color} ${config.border}`}
          >
            {config.label}
          </span>

          {/* Source Counter */}
          <div className="flex items-center gap-1.5 text-slate-400 dark:text-neutral-500 text-xs font-semibold">
            <FileText className="w-4 h-4" />
            {sourceCount} Sources
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotebookCard;
